import { InvalidArgumentError, NotSupportedError } from "../Errors";
import { reject } from "../TypingUtils";

import { Fetch } from "./FetchImplementation/Fetch";
import {
    HttpStatusCode,
    HttpStatusHandler,
    HttpStatusHandlers,
    IHttpClient,
    IHttpClientRequestWrapper,
    IHttpClientResponse,
    IHttpClientResponseSync,
    QueryParamsObject,
} from "./IHttpClient";
import { HttpClientAuthenticationProvider } from "./HttpClientAuthenticationProvider";

function trimTrailingSlash(url: string): string {
    return url.slice(-1) === "/" ? url.slice(0, -1) : url;
}

function trimLeadingSlash(url: string): string {
    return url[0] === "/" ? url.slice(1) : url;
}

type BodyFactory = () => BodyInit | null;

interface IHttpClientRequestConfig {
    signal?: AbortSignal;
    bodyFactory?: BodyFactory;
    acceptCodes: HttpStatusCode[];
    httpStatusHandlers: HttpStatusHandlers;
    queryParams: QueryParamsObject;
    contentType?: string;
}

export interface IExceptionLoggerParams {
    fetchUrl?: string;
}
export type ExceptionLogger = (error: unknown, params?: IExceptionLoggerParams) => void;

export class HttpClientResponse implements IHttpClientResponse {
    private readonly fetchResponse: Response;

    public constructor(fetchResponse: Response) {
        this.fetchResponse = fetchResponse;
    }

    public get status(): HttpStatusCode {
        return this.fetchResponse.status;
    }

    public get headers(): Headers {
        return this.fetchResponse.headers;
    }

    public async json<T>(): Promise<T> {
        const responseData = await this.fetchResponse.json();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return responseData;
    }

    public async blob(): Promise<Blob> {
        const responseData = await this.fetchResponse.blob();
        return responseData;
    }

    public async text(): Promise<string> {
        const responseData = await this.fetchResponse.text();
        return responseData;
    }
}

type RequestExecutionFunction = (config: IHttpClientRequestConfig) => Promise<HttpClientResponse | Response>;

export class HttpClientRequestWrapper implements IHttpClientRequestWrapper {
    private readonly executeRequest: RequestExecutionFunction;
    private readonly acceptCodes: HttpStatusCode[] = [];
    private httpStatusHandlers: HttpStatusHandlers = {};
    private queryParams: QueryParamsObject = {};
    private signal: undefined | AbortSignal;
    private bodyFactory?: BodyFactory;
    private contentType?: string;

    public constructor(execute: RequestExecutionFunction) {
        this.executeRequest = execute;
    }

    public withAbortSignal(signal: undefined | AbortSignal): this {
        this.signal = signal;
        return this;
    }

    public withContentType(contentType: string): this {
        this.contentType = contentType;
        return this;
    }

    public withBodyFromBuffer(body: false | Buffer | undefined): this {
        if (body === false || body == undefined) {
            return this;
        }
        this.bodyFactory = () => body;
        return this;
    }

    public withJsonBody(serializableBody: unknown | {} | unknown[] | undefined): this {
        if (serializableBody !== false && serializableBody != undefined) {
            this.bodyFactory = () => JSON.stringify(serializableBody);
        }
        return this;
    }

    public withQueryParams(queryParams: QueryParamsObject | false | undefined): this {
        if (queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
            };
        }
        return this;
    }

    public withRawBody(body: Buffer | string | false | undefined): this {
        if (body !== false && body != undefined) {
            this.bodyFactory = () => body;
        }
        return this;
    }

    public withHttpStatusHandler(statusCode: HttpStatusCode, handler: HttpStatusHandler): this {
        this.httpStatusHandlers = {
            ...this.httpStatusHandlers,
            [statusCode]: handler,
        };
        return this;
    }

    public withHttpStatusHandlers(httpStatusHandlers: HttpStatusHandlers): this {
        this.httpStatusHandlers = { ...httpStatusHandlers };
        return this;
    }

    public async json<T>(): Promise<T> {
        const result = await this;
        return result.json<T>();
    }

    public async blob(): Promise<Blob> {
        const result = await this;
        return result.blob();
    }

    public async text(): Promise<string> {
        const result = await this;
        return result.text();
    }

    public acceptCode(code: HttpStatusCode): this {
        this.acceptCodes.push(code);
        return this;
    }

    public readonly then: Promise<HttpClientResponse>["then"] = (...args) => {
        const resultPromise = (async () => {
            const result = await this.executeRequest(this.createConfig());
            if (result instanceof HttpClientResponse) {
                return result;
            } else {
                return new HttpClientResponse(result);
            }
        })();
        return resultPromise.then(...args);
    };

    private createConfig(): IHttpClientRequestConfig {
        return {
            signal: this.signal,
            bodyFactory: this.bodyFactory,
            acceptCodes: this.acceptCodes,
            httpStatusHandlers: this.httpStatusHandlers,
            queryParams: this.queryParams,
            contentType: this.contentType,
        };
    }

    public static create(fn: RequestExecutionFunction): HttpClientRequestWrapper {
        return new HttpClientRequestWrapper(fn);
    }
}

export class HttpClient implements IHttpClient {
    private readonly fetchImpl: Fetch;
    private readonly baseUrl: undefined | string;
    private readonly authenticationProvider: HttpClientAuthenticationProvider;
    private readonly defaultParams?: QueryParamsObject = {};
    private readonly exceptionLogger?: ExceptionLogger;
    private readonly httpStatusHandlers: HttpStatusHandlers;

    public constructor(
        fetchImpl: Fetch,
        baseUrl: undefined | string,
        authenticationProvider: HttpClientAuthenticationProvider,
        defaultQueryParams?: QueryParamsObject,
        exceptionLogger?: ExceptionLogger,
        httpStatusHandlers?: HttpStatusHandlers
    ) {
        this.fetchImpl = fetchImpl;
        this.baseUrl = baseUrl == undefined ? baseUrl : trimTrailingSlash(baseUrl);
        this.defaultParams = defaultQueryParams;
        this.authenticationProvider = authenticationProvider;
        this.exceptionLogger = exceptionLogger;
        this.httpStatusHandlers = { ...httpStatusHandlers };
    }

    public get(url: string): IHttpClientRequestWrapper {
        return this.method("GET")(url);
    }

    public post(url: string): IHttpClientRequestWrapper {
        return this.method("POST")(url);
    }

    public put(url: string): IHttpClientRequestWrapper {
        return this.method("PUT")(url);
    }

    public patch(url: string): IHttpClientRequestWrapper {
        return this.method("PATCH")(url);
    }

    public delete(url: string): IHttpClientRequestWrapper {
        return this.method("DELETE")(url);
    }

    private method(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"): (url: string) => IHttpClientRequestWrapper {
        return (url: string) =>
            HttpClientRequestWrapper.create(async config => {
                const fetchUrl = this.url(url, config.queryParams);
                let request = this.appendDefaultRequestHeaders({ method: method }, config.contentType);
                request = this.authenticationProvider(request);
                if (config.signal) {
                    request.signal = config.signal;
                }
                if (config.bodyFactory) {
                    if (method === "GET") {
                        throw new InvalidArgumentError("method", "Body is not allowed for GET requests");
                    }
                    request.body = config.bodyFactory();
                }

                try {
                    const response = await this.fetchImpl(fetchUrl, request);
                    await this.checkStatus(response, config);
                    return response;
                } catch (e) {
                    this.exceptionLogger?.(e, { fetchUrl: fetchUrl });
                    return reject(e as Error);
                }
            });
    }

    private appendDefaultRequestHeaders(options: RequestInit, contentType?: string): RequestInit {
        options.headers = options.headers ?? {};
        // @ts-ignore
        options.headers["Content-Type"] = contentType ?? "application/json";
        // @ts-ignore
        options.headers["Cache-Control"] = "no-cache, no-store";
        // @ts-ignore
        options.headers["Pragma"] = "no-cache";
        // @ts-ignore
        options.headers["Expires"] = "0";
        return options;
    }

    public url(path: string, params: undefined | QueryParamsObject): string {
        let result = path;
        if (this.baseUrl != undefined) {
            result = this.baseUrl + "/" + trimLeadingSlash(result);
        }
        const resultParams = { ...this.defaultParams, ...params };
        if (path.includes("?") && params != undefined && Object.keys(resultParams).length > 0) {
            throw new NotSupportedError(`Passing search params in url and via params object is not supported`);
        }
        result = result + this.createQueryString(resultParams);
        return result;
    }

    private async checkStatus(response: Response, config: IHttpClientRequestConfig): Promise<void> {
        const isSuccessResponseStatus = response.status >= 200 && response.status < 300;
        const isAcceptCodesIncludesResponseStatus = (config.acceptCodes ?? []).includes(response.status);

        if (!!config.httpStatusHandlers[response.status]) {
            await config.httpStatusHandlers[response.status]?.(response);
        } else if (!!this.httpStatusHandlers[response.status]) {
            await this.httpStatusHandlers[response.status]?.(response);
        }

        if (!isSuccessResponseStatus && !isAcceptCodesIncludesResponseStatus) {
            const errorText = await response.text();
            let serverResponse;
            try {
                serverResponse = JSON.parse(errorText);
            } catch {
                serverResponse = undefined;
            }
            if (serverResponse != undefined) {
                if ("error" in serverResponse && typeof serverResponse.error === "object") {
                    throw new HttpError(JSON.stringify(serverResponse.error), response.status);
                }
                throw new HttpError(
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    serverResponse.Message ?? serverResponse.message ?? serverResponse.Error,
                    response.status
                );
            }
            throw new HttpError(errorText, response.status);
        }
    }

    public postSync(url: string, params: undefined | QueryParamsObject, data: {}): IHttpClientResponseSync {
        const request = new XMLHttpRequest();
        request.open("POST", this.url(url, params), false);
        request.withCredentials = true;
        request.setRequestHeader("Content-Type", "application/json; charset=UTF-8");
        request.send(JSON.stringify(data));
        return {
            status: request.status,
            json: <T>() => JSON.parse(request.responseText) as T,
        };
    }

    private makeParamValueString(param: string, value: string): string {
        return `${encodeURIComponent(param)}=${encodeURIComponent(value)}`;
    }

    private createQueryString(params?: QueryParamsObject): string {
        if (params == undefined) {
            return "";
        }
        const params2 = params;
        let result = Object.keys(params)
            .map(key => {
                const value = params2[key];
                if (typeof value === "string") {
                    return this.makeParamValueString(key, value);
                }
                if (typeof value === "number" || typeof value === "boolean") {
                    return this.makeParamValueString(key, value.toString());
                }
                if (Array.isArray(value)) {
                    return value
                        .map(item => {
                            if (typeof item === "string") {
                                return this.makeParamValueString(key, item);
                            }
                            if (typeof item === "number") {
                                return this.makeParamValueString(key, item.toString());
                            }
                            return undefined;
                        })
                        .filter(x => x != undefined)
                        .join("&");
                }
                return undefined;
            })
            .filter(x => x != undefined)
            .join("&");
        result = result ? "?" + result : "";
        return result;
    }

    public createFetchImpl(): Fetch {
        const fetchWithOptions = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            if (typeof input === "string" || input instanceof URL) {
                const fetchUrl = this.url(input.toString(), undefined);
                init = init ?? {};
                init.headers = init.headers ?? {};
                // @ts-ignore
                init.headers["Cache-Control"] = "no-cache, no-store";
                // @ts-ignore
                init.headers["Pragma"] = "no-cache";
                // @ts-ignore
                init.headers["Expires"] = "0";
                init = this.authenticationProvider(init);
                return this.fetchImpl(fetchUrl, init);
            } else {
                const fetchUrl = this.url(input.url, undefined);
                let requestInit: RequestInit = {
                    headers: {
                        ["Cache-Control"]: "no-cache, no-store",
                        ["Pragma"]: "no-cache",
                        ["Expires"]: "0",
                        ...input.headers,
                    },
                    signal: input.signal,
                    cache: input.cache,
                    credentials: input.credentials,
                    integrity: input.integrity,
                    keepalive: input.keepalive,
                    method: input.method,
                    mode: input.mode,
                    redirect: input.redirect,
                    referrer: input.referrer,
                    referrerPolicy: input.referrerPolicy,
                };
                requestInit = this.authenticationProvider(requestInit);
                return this.fetchImpl(fetchUrl, requestInit);
            }
        };
        return fetchWithOptions;
    }
}

export class HttpError extends Error {
    public readonly statusCode: number;

    public constructor(message: string, statusCode: number) {
        super(message);
        this.name = "HttpError";
        this.statusCode = statusCode;
    }

    public override get message(): string {
        return super.message + `. StatusCode: ${this.statusCode}`;
    }
}

export const getContentType = (response: IHttpClientResponse): string | null => response.headers.get("Content-Type");

export const getFileName = (response: IHttpClientResponse): string | undefined =>
    response.headers.get("Content-Disposition")?.split(";")[1]?.split("=")[1]?.replace(/['"]/g, "");
